'use client';

import { sexSchema } from '@coas/validators/const';
import { useEffect, useMemo } from 'react';
import { api } from 'trpc/react';
import type { RouterOutputs } from 'trpc/shared';
import { BuyerButton } from '~/features/BuyerButton';
import { ProductCard, ProductCardSkeleton } from '~/features/ProductCard';
import { useProductApiFilter } from '~/features/ProductFilter';
import { useProductSorting } from '~/features/ProductSorting';
import { VirtualGrid } from '~/features/VirtualGrid';
import { useSession } from '~/shared/lib/auth';
import { gridConfig } from '../config/gridConfig';
import { ProductGridHeader } from './ProductGridHeader';

type Product = RouterOutputs['product']['getProductsInfinite']['products'][number];

type ProductGridProps = {
    category: RouterOutputs['category']['getBySlug'] | null;
    list?: RouterOutputs['list']['getBySlug'] | null;
    search?: string;
};

export default ({ category, list, search }: ProductGridProps) => {
    const [sorting] = useProductSorting();
    const apiFilter = useProductApiFilter();

    const { data: session } = useSession();

    const productQuery = api.product.getProductsInfinite.useInfiniteQuery(
        {
            limit: 20,
            sorting,
            categoryId: category?.item.id,
            listId: list?.item.id,
            search,
            filter: apiFilter,
        },
        {
            getNextPageParam: lastPage => lastPage.nextCursor,
            refetchOnMount: false,
        },
    );

    const products = useMemo((): Product[] => {
        if (!productQuery.data) return [];

        return productQuery.data.pages.flatMap(page => page.products ?? []);
    }, [productQuery.data]);

    const showBuyerButton = useMemo(() => !session && products.length > 0, [session, products]);

    useEffect(() => {
        if (sorting) window.scrollTo({ top: 0 });
    }, [sorting]);

    return (
        <>
            <ProductGridHeader category={category} />
            <div className={'flex w-full max-w-grid flex-1 flex-col self-center px-page'}>
                <VirtualGrid
                    query={productQuery}
                    data={products}
                    gridConfig={gridConfig}
                    loadingSource={
                        <>
                            {(!category || category.sex === sexSchema.enum.man) && (
                                <source src={'/assets/loading-man.mp4'} type={'video/mp4'} />
                            )}
                            {category?.sex === sexSchema.enum.woman && (
                                <source src={'/assets/loading-woman.mp4'} type={'video/mp4'} />
                            )}
                        </>
                    }
                    itemBuilder={product => <ProductCard key={product.id} product={product} />}
                    skeletonItemBuilder={index => <ProductCardSkeleton key={index} />}
                />

                <BuyerButton hidden={!showBuyerButton} />
            </div>
        </>
    );
};
